<template>
  <div>
    <!-- Show icon when heartbeat received OR when there are active jobs -->
    <v-tooltip location="start" v-if="heartbeatReceived || jobs.length > 0">
      <template v-slot:activator="{ props: tooltipProps }">
        <v-btn icon variant="plain" v-bind="tooltipProps" @click="dialogOpen = true">
          <template v-if="activeJobCount > 0">
            <v-badge :content="activeJobCount" color="error" overlap>
              <v-icon>mdi-briefcase-clock</v-icon>
            </v-badge>
          </template>
          <template v-else>
            <v-icon>mdi-briefcase-check</v-icon>
          </template>
        </v-btn>
      </template>
      <div class="text-body-2">
        <template v-if="activeJobCount > 0">
          {{ activeJobCount }} active job{{ activeJobCount > 1 ? 's' : '' }}
        </template>
        <template v-else>
          No active jobs
        </template>
      </div>
    </v-tooltip>

    <!-- Job History Dialog -->
    <v-dialog v-model="dialogOpen" max-width="600" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-briefcase</v-icon>
          Job Activity
          <v-spacer />
          <v-btn icon variant="text" size="small" @click="dialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- Active Jobs Section -->
        <template v-if="activeJobCount > 0">
          <v-card-subtitle class="pt-3 pb-1 text-subtitle-2">Active Jobs</v-card-subtitle>
          <v-list density="compact">
            <v-list-item v-for="job in activeJobsList" :key="job.id">
              <template v-slot:prepend>
                <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
              </template>
              <v-list-item-title>{{ job.description }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ job.state }}
              </v-list-item-subtitle>
              <div v-if="job.details" class="text-caption text-medium-emphasis mt-1">
                <div v-for="(value, key) in job.details" :key="key">
                  {{ key }}: {{ value }}
                </div>
              </div>
            </v-list-item>
          </v-list>
          <v-divider />
        </template>

        <!-- Event History Section -->
        <v-card-subtitle class="pt-3 pb-1 d-flex align-center">
          <span class="text-subtitle-2">Recent Activity</span>
          <v-spacer />
          <v-btn
            v-if="eventHistory.length > 0"
            variant="text"
            size="small"
            color="error"
            @click="clearHistory"
          >
            Clear
          </v-btn>
        </v-card-subtitle>

        <v-card-text style="max-height: 400px; overflow-y: auto;" class="pa-0">
          <v-list v-if="eventHistory.length > 0" density="compact">
            <v-tooltip
              v-for="event in eventHistory"
              :key="event.id"
              location="start"
              :disabled="!hasEventDetails(event)"
            >
              <template v-slot:activator="{ props: tooltipProps }">
                <v-list-item
                  v-bind="tooltipProps"
                  :class="getEventClass(event.eventType)"
                >
                  <template v-slot:prepend>
                    <v-icon :color="getEventColor(event.eventType)" size="small" class="mr-2">
                      {{ getEventIcon(event.eventType) }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="text-body-2">
                    {{ event.description }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span class="text-capitalize">{{ getEventLabel(event.eventType) }}</span>
                    <span class="mx-1">-</span>
                    <span>{{ formatTime(event.timestamp) }}</span>
                    <template v-if="event.errorMessage">
                      <br />
                      <span class="text-error">{{ event.errorMessage }}</span>
                    </template>
                  </v-list-item-subtitle>
                </v-list-item>
              </template>
              <div class="text-body-2">
                <div class="font-weight-bold mb-1">{{ event.description }}</div>
                <div v-if="event.jobId" class="text-caption">Job ID: {{ event.jobId }}</div>
                <div v-if="event.state" class="text-caption">State: {{ event.state }}</div>
                <template v-if="event.details">
                  <div v-for="(value, key) in event.details" :key="key" class="text-caption">
                    {{ key }}: {{ value }}
                  </div>
                </template>
                <div v-if="event.errorMessage" class="text-caption text-error mt-1">
                  Error: {{ event.errorMessage }}
                </div>
                <div class="text-caption mt-1">{{ formatDateTime(event.timestamp) }}</div>
              </div>
            </v-tooltip>
          </v-list>
          <div v-else class="text-center text-medium-emphasis pa-8">
            No recent activity
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="text-caption text-medium-emphasis px-4">
          <v-icon size="small" class="mr-1">mdi-connection</v-icon>
          <template v-if="heartbeatReceived">
            Connected
            <span v-if="lastHeartbeat" class="ml-1">
              (last ping: {{ formatTime(lastHeartbeat) }})
            </span>
          </template>
          <template v-else>
            Connecting...
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useJobStore } from '@/stores'
import { storeToRefs } from 'pinia'

const jobStore = useJobStore()
const { jobs, eventHistory, heartbeatReceived, lastHeartbeat } = storeToRefs(jobStore)
const { clearHistory } = jobStore

const dialogOpen = ref(false)

const ACTIVE_STATES = ['Queued', 'Processing', 'Enqueued', 'Running', 'Awaiting', 'Calculating', 'Initializing', 'Aggregating', 'Saving']

const activeJobsList = computed(() => {
  return jobs.value.filter(j => ACTIVE_STATES.includes(j.state))
})

const activeJobCount = computed(() => activeJobsList.value.length)

function formatTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function getEventIcon(eventType) {
  switch (eventType) {
    case 'job.started': return 'mdi-play-circle-outline'
    case 'job.completed': return 'mdi-check-circle-outline'
    case 'job.failed': return 'mdi-alert-circle-outline'
    case 'job.progress': return 'mdi-progress-clock'
    default: return 'mdi-circle-outline'
  }
}

function getEventColor(eventType) {
  switch (eventType) {
    case 'job.started': return 'info'
    case 'job.completed': return 'success'
    case 'job.failed': return 'error'
    case 'job.progress': return 'warning'
    default: return 'grey'
  }
}

function getEventLabel(eventType) {
  switch (eventType) {
    case 'job.started': return 'Started'
    case 'job.completed': return 'Completed'
    case 'job.failed': return 'Failed'
    case 'job.progress': return 'Progress'
    default: return eventType
  }
}

function getEventClass() {
  // Optional: add subtle background colors for different event types
  return ''
}

function hasEventDetails(event) {
  return event.jobId || event.state || event.details || event.errorMessage
}

function formatDateTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}
</script>
